interface WalletTotals {
  readonly publicKey: string;
  readonly totalVolume: string;
  readonly totalTrades: number;
}

interface ProfileRow {
  readonly totalVolume?: string;
  readonly totalTrades?: number | null;
  readonly wallets?: readonly WalletTotals[];
}

/**
 * A REST snapshot is read before some live events land, so replacing the cache
 * with it would regress the lifetime totals the client already advanced.
 * Wallet totals are monotonic (max-merge, like the live reducer); the incoming
 * wallet set stays authoritative. Everything else, including the streak (which
 * is server-owned), is the server's. Returns `incoming` by reference when
 * nothing is merged.
 */
export function mergeUserProfileSnapshot<R extends ProfileRow>(previous: R, incoming: R): R {
  if (previous.wallets === undefined || incoming.wallets === undefined) return incoming;
  const known = new Map(previous.wallets.map((wallet) => [wallet.publicKey, wallet]));
  let changed = false;
  const wallets = incoming.wallets.map((wallet) => {
    const before = known.get(wallet.publicKey);
    if (before === undefined) return wallet;
    const totalVolume =
      BigInt(before.totalVolume) > BigInt(wallet.totalVolume)
        ? before.totalVolume
        : wallet.totalVolume;
    const totalTrades = Math.max(before.totalTrades, wallet.totalTrades);
    if (totalVolume === wallet.totalVolume && totalTrades === wallet.totalTrades) return wallet;
    changed = true;
    return { ...wallet, totalVolume, totalTrades };
  });
  if (!changed) return incoming;
  return {
    ...incoming,
    wallets,
    totalVolume: wallets.reduce((sum, wallet) => sum + BigInt(wallet.totalVolume), 0n).toString(),
    totalTrades: wallets.reduce((sum, wallet) => sum + wallet.totalTrades, 0),
  };
}
